import { buildEdgeAuthStartUrl } from '@cortex/core-types';
import { logError } from '@cortex/faro';
import { config } from '../config.js';
import type {
  AuthUrlResponse,
  CurrentUserResponse,
  ScoringRunInput,
  ScoringResultWithId,
  ScoringHistoryResponse,
  BrandsResponse,
  CategoriesResponse,
  BrandLinesResponse,
  UpdateBrandLineResponse,
  BrandLineData,
} from '../types/api.js';

/**
 * @graph-stack mall
 * @graph-domain Operations
 * @graph-business APIエラークラス。HTTPステータス・エラーコード付きのカスタムError
 * @graph-connects none
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// エラーレスポンスの型
interface ErrorResponse {
  error?: {
    message?: string;
    code?: string;
  };
}

/**
 * 汎用フェッチラッパー
 * @graph-connects /api/* [calls] Mall Scorer API の各エンドポイントへの HTTP リクエスト
 */
async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...Object.fromEntries(
      options.headers instanceof Headers
        ? options.headers.entries()
        : Object.entries(options.headers ?? {}),
    ),
  };

  const res = await fetch(`${config.apiUrl}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (res.status === 401) {
    // Edge Auth Worker から 401 が返ったら Edge Auth start に遷移。それ以外は旧 /login へ。
    if (res.headers.get('X-Edge-Auth-Start')) {
      window.location.href = buildEdgeAuthStartUrl(window.location.href);
    } else if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    throw new ApiError('Unauthorized', 401, 'UNAUTHORIZED');
  }

  if (!res.ok) {
    const err = (await res.json().catch((error: unknown) => {
      // エラーレスポンス本体が JSON でない場合は HTTP ステータスのみで報告する
      logError(error instanceof Error ? error : new Error(String(error)), {
        event: 'error_response_body_parse_failed',
      });
      return {} as ErrorResponse;
    })) as ErrorResponse;
    throw new ApiError(
      err.error?.message ?? `HTTP ${String(res.status)}`,
      res.status,
      err.error?.code,
    );
  }

  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

/**
 * @graph-stack mall
 * @graph-domain Operations
 * @graph-business スコアリングAPIクライアント。run/getHistory/getByIdメソッドでスコアリングエンドポイントを呼び出し
 * @graph-connects /scoring/run [calls] スコアリング実行API
 * @graph-connects /scoring/history [calls] スコアリング履歴取得API
 * @graph-connects /scoring/:id [calls] スコアリング結果取得API
 */
export const scoringApi = {
  run: (input: ScoringRunInput): Promise<ScoringResultWithId> =>
    request<ScoringResultWithId>('/scoring/run', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  getHistory: (limit?: number): Promise<ScoringHistoryResponse> => {
    const query = limit ? `?limit=${String(limit)}` : '';
    return request<ScoringHistoryResponse>(`/scoring/history${query}`);
  },
  getById: (id: string): Promise<ScoringResultWithId> =>
    request<ScoringResultWithId>(`/scoring/${encodeURIComponent(id)}`),
};

/** @graph-connects none */
interface SeedResponse {
  success: boolean;
  message: string;
  count: number;
}

/**
 * @graph-stack mall
 * @graph-domain Operations
 * @graph-business マスタデータAPIクライアント。getBrands/getCategories/getBrandLines/updateBrandLine/seedメソッドでマスタエンドポイントを呼び出し
 * @graph-connects /master/brands [calls] ブランドマスタ取得・初期データ投入API
 * @graph-connects /master/categories [calls] カテゴリマスタ取得・初期データ投入API
 * @graph-connects /master/brand-lines [calls] ブランドラインマスタ取得・更新・初期データ投入API
 */
export const masterApi = {
  getBrands: (): Promise<BrandsResponse> => request<BrandsResponse>('/master/brands'),
  getCategories: (): Promise<CategoriesResponse> =>
    request<CategoriesResponse>('/master/categories'),
  getBrandLines: (): Promise<BrandLinesResponse> =>
    request<BrandLinesResponse>('/master/brand-lines'),
  updateBrandLine: (key: string, data: BrandLineData): Promise<UpdateBrandLineResponse> =>
    request<UpdateBrandLineResponse>(`/master/brand-lines/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  seedBrands: (): Promise<SeedResponse> =>
    request<SeedResponse>('/master/brands/seed', { method: 'POST' }),
  seedCategories: (): Promise<SeedResponse> =>
    request<SeedResponse>('/master/categories/seed', { method: 'POST' }),
  seedBrandLines: (): Promise<SeedResponse> =>
    request<SeedResponse>('/master/brand-lines/seed', { method: 'POST' }),
};

/**
 * @graph-stack mall
 * @graph-domain Operations
 * @graph-business 認証APIクライアント。getAuthUrl/getCurrentUser/logoutメソッドで認証エンドポイントを呼び出し
 * @graph-connects /auth/login [calls] 認証URL取得API
 * @graph-connects /auth/me [calls] 現在のユーザーセッション取得API
 * @graph-connects /auth/logout [calls] ログアウトAPI
 */
export const authApi = {
  getAuthUrl: () => request<AuthUrlResponse>('/auth/login'),
  getCurrentUser: () =>
    request<CurrentUserResponse>('/auth/me').catch((error: unknown) => {
      // 認証 API 失敗時は未ログイン扱い。UI 側で /login へリダイレクトする
      logError(error instanceof Error ? error : new Error(String(error)), {
        event: 'auth_check_failed',
      });
      return null;
    }),
  logout: () => request<undefined>('/auth/logout', { method: 'POST' }),
};
